import {
  GatewayRule,
  CheckoutConfig,
  GatewayCredentials,
  DunningRule,
  EmailTemplate,
  PortalBranding,
  AnalyticsConfig,
} from '../../types';

export const DEFAULT_GATEWAY_RULES: GatewayRule[] = [
  { id: 'gr-1', gateway: 'stripe', currencies: ['*'], countries: ['*'], isEnabled: true },
  { id: 'gr-2', gateway: 'razorpay', currencies: ['INR'], countries: ['IN'], isEnabled: true },
];

export const DEFAULT_DUNNING_RULES: DunningRule[] = [
  {
    id: 'dr-1',
    name: 'Standard SaaS Policy',
    isEnabled: true,
    gracePeriodDays: 3,
    maxDunningDays: 7,
    steps: [
      { id: 's1', dayDelay: 0, emailTemplateId: 'tpl-retry-1', action: 'retry' },
      { id: 's2', dayDelay: 3, emailTemplateId: 'tpl-retry-2', action: 'retry' },
      { id: 's3', dayDelay: 7, emailTemplateId: 'tpl-final-warning', action: 'mark_past_due' },
    ],
  },
];

export const DEFAULT_TEMPLATES: EmailTemplate[] = [
  {
    id: 'tpl-retry-1',
    name: 'Payment Update - Gentle Continuity Notice',
    category: 'Billing',
    subject: 'Keep your {{company_name}} workflows running uninterrupted ⚡',
    body: 'Hi {{customer_name}},\n\nWe encountered a temporary issue while renewing your {{company_name}} subscription. To ensure your workspaces, automated pipelines, and team access remain active without interruption, please take 30 seconds to update your card:\n\n👉 Update Payment Method: {{payment_link}}\n\nNeed assistance or want to adjust your plan? Our success team is on standby to help.\n\nBest regards,\nThe {{company_name}} Billing Team',
    lastModified: new Date().toISOString(),
  },
  {
    id: 'tpl-retry-2',
    name: 'Payment Update - Value & Account Protection',
    category: 'Billing',
    subject: 'Protect your workspace data & active quota for {{company_name}}',
    body: 'Hi {{customer_name}},\n\nWe want to ensure you don\'t lose access to your priority features and active models. Your subscription renewal is still pending.\n\nYou can keep all your data, workspaces, and team licenses running smoothly by updating your payment details:\n\n👉 Secure 1-Click Update: {{payment_link}}\n\n💡 Scaling up or need to switch to an annual plan with 2 months free? Reply directly to this email.\n\nWarm regards,\n{{company_name}} Team',
    lastModified: new Date().toISOString(),
  },
  {
    id: 'tpl-recovery-1',
    name: 'Abandoned Checkout - Instant Activation Incentive',
    category: 'Recovery',
    subject: 'Your {{plan_name}} workspace is ready — activate in 1 click (Bonus Credits Included)',
    body: "Hi there,\n\nWe noticed you started setting up {{plan_name}}! Your workspace configuration has been saved and is ready for immediate deployment.\n\nComplete your activation today to unlock:\n✅ Instant access to high-throughput compute\n✅ 14-day 100% money-back guarantee\n✅ Dedicated team onboarding\n\n👉 Finish Activation: {{payment_link}}\n\nLet's build something incredible together!",
    lastModified: new Date().toISOString(),
  },
];

export const DEFAULT_CREDENTIALS: GatewayCredentials = {
  stripe: { publishableKey: '', secretKey: '', webhookSecret: '' },
  razorpay: { keyId: '', keySecret: '', webhookSecret: '' },
  cashfree: { appId: '', secretKey: '', environment: 'production' },
  lemonsqueezy: { apiKey: '', webhookSecret: '' },
  paddle: { vendorId: '', authCode: '', publicKey: '' },
  checkoutcom: { publicKey: '', secretKey: '', channelId: '' },
  chargebee: { site: '', publishableKey: '' },
};

export const DEFAULT_PORTAL_BRANDING: PortalBranding = {
  name: 'Client Billing Portal',
  logoUrl: 'https://cdn-icons-png.flaticon.com/512/5968/5968322.png',
  footerText: '© 2024 Secure Payments powered by SubScale. All rights reserved.',
};

export const DEFAULT_ANALYTICS_CONFIG: AnalyticsConfig = {
  googleAnalyticsId: (import.meta as any).env?.VITE_GA_MEASUREMENT_ID || '',
  isGoogleAnalyticsEnabled: Boolean((import.meta as any).env?.VITE_GA_MEASUREMENT_ID),
  facebookPixelId: (import.meta as any).env?.VITE_FB_PIXEL_ID || '',
  isFacebookPixelEnabled: Boolean((import.meta as any).env?.VITE_FB_PIXEL_ID),
  clarityProjectId: (import.meta as any).env?.VITE_CLARITY_PROJECT_ID || '',
  isClarityEnabled: Boolean((import.meta as any).env?.VITE_CLARITY_PROJECT_ID),
  trackPageViews: true,
  trackPurchases: true,
  trackCheckouts: true,
};

export const DEFAULT_CHECKOUT_CONFIG: CheckoutConfig = {
  template: 'cyberpunk',
  logoUrl: 'https://cdn-icons-png.flaticon.com/512/5968/5968322.png',
  paymentDisplayName: 'SubScale',
  primaryColor: '#ff00ff',
  secondaryColor: '#10b981',
  backgroundColor: '#0f172a',
  textColor: '#f8fafc',
  fontFamily: 'Inter',
  buttonText: 'Complete Purchase',
  buttonRadius: '1rem',
  buttonShadow: '0 20px 25px -5px rgb(0 0 0 / 0.1)',
  inputStyle: 'solid',
  showSecurityBadge: true,
  allowCoupons: true,
  couponPlaceholder: 'ENTER PROMO CODE',
  couponDiscountPercentage: 10,
  headerMain: 'Secure Checkout',
  headerSub: 'Join thousands of teams scaling with SubScale.',
  showSocialProof: true,
  socialProofText: 'Trusted by 500+ SaaS teams',
  showFomo: true,
  fomoMessage: '🔥 14 people joined this plan today!',
  showTestimonial: true,
  testimonial: {
    quote: 'SubScale transformed our billing overnight. Highly recommended!',
    author: 'Sarah Jenkins',
    role: 'CEO, CloudFlow',
    avatarUrl: 'https://i.pravatar.cc/150?u=sarah',
  },
  showTrustBar: true,
  trustLogos: [
    'https://upload.wikimedia.org/wikipedia/commons/a/a9/Amazon_logo.svg',
    'https://upload.wikimedia.org/wikipedia/commons/2/2f/Google_2015_logo.svg',
    'https://upload.wikimedia.org/wikipedia/commons/0/08/Netflix_2015_logo.svg',
  ],
  showGuarantee: true,
  guaranteeText: '30-Day Money-Back Guarantee',
  termsUrl: 'https://subscale.io/terms',
  footerText: 'Need help? Contact support@subscale.io',
  quarterlyDiscountPercentage: 0,
  yearlyDiscountPercentage: 0,
  recoveryConfig: {
    isEnabled: true,
    automatedSequence: true,
    delayHours: 1,
    abandonmentThresholdMin: 15,
    templateId: 'tpl-recovery-1',
  },
  addons: [
    { id: 'addon-1', name: 'Priority Support', price: 29, description: '24/7 dedicated support line.', isEnabled: false },
    { id: 'addon-2', name: 'White-labeling', price: 99, description: 'Remove SubScale branding.', isEnabled: false },
  ],
  useMultiStep: true,
  showTaxBreakdown: true,
  enableQuantity: true,
  quantityLabel: 'Licenses',
  maxQuantity: 100,
  customFields: [
    { id: 'name', label: 'Full Name', placeholder: 'Enter your name', type: 'text', required: true, isEnabled: true },
    { id: 'email', label: 'Email Address', placeholder: 'name@company.com', type: 'email', required: true, isEnabled: true },
    { id: 'phone', label: 'Mobile Number', placeholder: '+1 (555) 000-0000', type: 'tel', required: true, isEnabled: true },
  ],
  abTests: [],
};
